<template>
<v-container>
  <v-form
    ref="form"
    lazy-validation
  >
    <maoc-card>
      <request-time ref="requestTime"></request-time>
    </maoc-card>

    <maoc-card :title="$t('request_type')">
      <request-type ref="requestData" @requestUpdated="updateRequest"></request-type>
    </maoc-card>

    <maoc-card :title="$t('request_entity')">
      <request-entity ref="entity"></request-entity>
    </maoc-card>

    <maoc-card title="Vessel Details" v-if="selectedRequest.type === 'vessel'">
      <vessel-details
        :requestCategory="selectedRequest.category"
        ref="vesselDetails"
      ></vessel-details>
    </maoc-card>

    <maoc-card title="Aeroplane Details" v-if="selectedRequest.type === 'aeroplane'">
      <aeroplane-details
        :requestCategory="selectedRequest.category"
        ref="aeroplanelDetails"
      />
    </maoc-card>

    <maoc-card title="Other Vessel Details" v-if="selectedRequest.type === 'other'">
      <others-details />
    </maoc-card>

    <maoc-card :title="$t('owner_data')">
      <owner-data ref="ownerData" />
    </maoc-card>

    <maoc-card :title="$t('crew_data')">
      <template v-slot:action-icon>
        <v-btn
          elevation=1
          medium
          plain
          @click="exportCrewManifest()"
        >
          <v-icon class="mdi mdi-file-excel"></v-icon> {{$i18n.locale}}
        </v-btn>
      </template>
      <crew-data
        ref="crewData"
        :vesselType="selectedRequest.type"
      ></crew-data>
    </maoc-card>

    <maoc-card :title="$t('other_linked_vessels')">
      <related-vessels ref="relatedVessels" />
    </maoc-card>

    <maoc-card :title="$t('summary_title')">
      <operation-summary
        ref="Summary"
        :vesselType="selectedRequest.type"
        :request="selectedRequest.requestType"
      />
    </maoc-card>

    <maoc-card
      :title="$t('photos_title')"
      :subtitle="$t('photos_message')"
      >
      <request-photos ref="photos" />
    </maoc-card>

    <!--
    <maoc-card :title="$t('attachments_title')" :subtitle="$t('attachments_message')">
      <request-attachments   ref="attachments" />
    </maoc-card>
    -->

    <maoc-card :title="$t('handling_codes')">
      <handling-codes ref="handlingCodes" />
    </maoc-card>

    <maoc-card :title="$t('source_evaluation')">
      <source-evaluation ref="sourceEval" />
    </maoc-card>

    <maoc-card :title="$t('intelligence_evaluation')">
      <intel-evaluation ref="intelEval" />
    </maoc-card>

    <v-row>
      <v-col>
        <v-alert
          v-model="hasErrors"
          border="top"
          color="red lighten-2"
          dark
          dismissible
        >
          <p>{{ requestErrorMessages(errorMessages.length) }}</p>
          <ul>
            <li v-for="message in errorMessages" :key="message">
              {{ message }}
            </li>
          </ul>

          <v-btn
            depressed
            block
            small
            color="error"
            @click="$vuetify.goTo(errorTarget)">
            {{ $t('go_to_error') }}
          </v-btn>
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-btn
          class="mr-2"
          @click="previewRequest">Preview
        </v-btn>

        <v-btn
          :disabled="!valid"
          class="mr-4" @click="exportData"
          :loading="loading"
        >
          Export Data
        </v-btn>

        <v-dialog
          v-model="resetDialog"
          persistent
          max-width="290"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="float-right"
              color="error"
              v-bind="attrs"
              v-on="on"
            >{{$t('reset')}}</v-btn>
          </template>

          <v-card>
            <v-card-title>
            </v-card-title>
            <v-card-text class="text-center">
              {{$t('reset_confirm')}}
            </v-card-text>
            <v-card-actions class="justify-center">
              <v-btn
                color="green darken-1"
                text
                @click="resetDialog = false"
              >
                {{ $t('no') }}
              </v-btn>
              <v-btn
                color="red darken-1"
                text
                @click="reset()"
              >
                {{ $t('yes')}}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-btn
          v-if="debugMode"
          class="float-right mr-4"
          @click="showDebugPreview">Debug Preview
        </v-btn>
      </v-col>
    </v-row>
  </v-form>

  <request-preview v-if="valid" :req="previewData" ref='requestPreview'></request-preview>

  <mail-link ref="formTarget" />

  <v-row>
    <v-col cols=12>
      <pre v-if="showDebug" id="debug_preview" class="preview_container" >
        {{ data }}
      </pre>
    </v-col>
  </v-row>

</v-container>
</template>

<script>
import { maocData } from '../services/MaocData'
import { defaultStyles } from '../services/Defaults'

import MailLink from './MailLink'
import RequestPreview from './RequestPreview'
import MaocCard from './ui/MaocCard'
import RequestType from './maoc_form/RequestType'
import RequestEntity from './maoc_form/RequestEntity'
import VesselDetails from './maoc_form/VesselDetails'
import AeroplaneDetails from './maoc_form/AeroplaneDetails.vue'
import RequestPhotos from './maoc_form/RequestPhotos'
import OwnerData from './maoc_form/OwnerData'
import CrewData from './maoc_form/CrewData'
import OthersDetails from './maoc_form/OthersDetails'
import RelatedVessels from './maoc_form/RelatedVessels'
import HandlingCodes from './maoc_form/HandlingCodes.vue'
import IntelEvaluation from './maoc_form/IntelEvaluation.vue'
import SourceEvaluation from './maoc_form/SourceEvaluation.vue'
import OperationSummary from './maoc_form/OperationSummary.vue'
import { ExportUtils, ImportUtils } from '../services/DataExport'
import RequestTime from './maoc_form/RequestTime.vue'

export default {
  name: 'MaocForm',
  data: vm => ({
    valid: false,
    showDebug: false,
    resetDialog: false,
    selectedRequest: {
      type: '',
      category: '',
      requestType: ''
    },
    sourceEvaluation: maocData.sourceEvaluation,
    styleOptions: defaultStyles,
    loading: false,
    hasErrors: false,
    errorMessages: [],
    errorTarget: {},
    data: {},
    previewDAta: {},
    exportRefs: [
      { key: 'request_time', ref: 'requestTime' },
      { key: 'request', ref: 'requestData' },
      { key: 'entity', ref: 'entity' },
      { key: 'vessel_details', ref: 'vesselDetails' },
      { key: 'aeroplane_details', ref: 'aeroplanelDetails' },
      { key: 'owner_data', ref: 'ownerData' },
      { key: 'crew_data', ref: 'crewData' },
      { key: 'related_vessels', ref: 'relatedVessels' },
      { key: 'summary', ref: 'Summary' },
      { key: 'handling_codes', ref: 'handlingCodes' },
      { key: 'source_evaluation', ref: 'sourceEval' },
      { key: 'intel_evaluation', ref: 'intelEval' }
    ],
    importRefs: [
      { key: 'request_time', ref: 'requestTime' },
      { key: 'entity', ref: 'entity' },
      { key: 'vessel_details', ref: 'vesselDetails' },
      { key: 'aeroplane_details', ref: 'aeroplanelDetails' },
      { key: 'owner_data', ref: 'ownerData' },
      { key: 'crew_data', ref: 'crewData' },
      { key: 'summary', ref: 'Summary' },
      { key: 'handling_codes', ref: 'handlingCodes' },
      { key: 'source_evaluation', ref: 'sourceEval' },
      { key: 'intel_evaluation', ref: 'intelEval' },
      { key: 'related_vessels', ref: 'relatedVessels' }
    ]
  }),
  components: {
    mailLink: MailLink,
    RequestPreview,
    MaocCard,
    RequestType,
    RequestEntity,
    VesselDetails,
    AeroplaneDetails,
    OthersDetails,
    OwnerData,
    CrewData,
    RelatedVessels,
    RequestPhotos,
    // RequestAttachments,
    HandlingCodes,
    IntelEvaluation,
    SourceEvaluation,
    OperationSummary,
    RequestTime
  },
  computed: {
    debugMode: function () {
      const debugOption = new URL(location.href).searchParams.get('debug')
      return debugOption !== null && debugOption !== ''
    }
  },
  methods: {
    previewRequest: function () {
      if (this.validate()) {
        this.valid = true
        this.previewData = this.generateData()
      }
    },
    validate () {
      this.valid = this.$refs.form.validate()
      this.hasErrors = !this.valid

      var errorTarget
      var messages = []
      this.$refs.form.inputs.forEach(function (input) {
        if (input.hasError) {
          if (!errorTarget) {
            errorTarget = input
          }
          messages = messages.concat(input.messagesToDisplay)
        }
      })

      this.errorMessages = messages
      this.errorTarget = errorTarget

      return this.valid
    },
    reset () {
      this.resetDialog = false
      this.$refs.form.reset()
      this.$refs.photos.reset()
      // this.$refs.attachments.reset()
      this.$nextTick(() => this.$refs.handlingCodes.setData({ h1: true, h2: true, h3: false }))
    },
    resetValidation () {
      this.$refs.form.resetValidation()
    },
    requestErrorMessages (count) {
      return this.$t('request_contains_errors').replace('[X]', count)
    },
    showDebugPreview () {
      this.showDebug = !this.showDebug
      if (this.showDebug) {
        this.data = this.generateData()
      }
    },
    getImageMetadata: function () {
      const images = this.$refs.photos.getImages()
      if (images) {
        return ExportUtils.packFileNames(images)
      }
    },
    getAttachmentsMetadata: function () {
      const attachments = this.$refs.attachments.getFiles()
      if (attachments) {
        return ExportUtils.packFileNames(attachments)
      }
    },
    importData: function (data) {
      ImportUtils.setComponentsDataModel(this.$refs, this.importRefs, data)
    },
    importImage: function (filename, blob) {
      this.$refs.photos.setImage(filename, blob)
    },
    importAttachment: function (filename, blob) {
      this.$refs.attachments.setFile(filename, blob)
    },
    generateData: function () {
      var dataModel = ExportUtils.getComponentsDataModel(this.$refs, this.exportRefs)
      dataModel.images = this.getImageMetadata()
      // dataModel.attachments = this.getAttachmentsMetadata()
      return dataModel
    },
    exportData () {
      this.loading = true
      // const subject = exporter.generateMailSubject()
      // const body = exporter.generateMailBody()

      // console.log(subject)
      // console.log(body)

      // this.$refs.formTarget.setTarget('alfmatos@gmail.com', subject, body)
      // this.$refs.mailPreview.setPreview(subject, body)
      this.data = this.generateData()
      const images = this.$refs.photos.getImages()
      const attachments = false // this.$refs.attachments.getFiles()
      var model = this
      ExportUtils.generatePackage(this.data, images, attachments, function () {
        model.loading = false
      })
    },
    exportCrewManifest () {
      this.$refs.crewData.exportCrewManifest()
    },
    formData () {
      this.data = {}
      this.data.date = this.dateFormatted
      return this.data
    },
    updateRequest (request) {
      this.selectedRequest = request
    },
    importFile: function (file) {
      var form = this
      var data = {}
      var jsZip = require('jszip')
      jsZip.loadAsync(file).then(function (zip) {
        Object.keys(zip.files).forEach(function (filename) {
          if (filename === 'data.json') {
            zip.files[filename].async('string').then(function (fileData) {
              data = JSON.parse(fileData)
              form.importData(data)
            })
          }

          if (filename.startsWith('images/') && filename !== 'images/') {
            zip.files[filename].async('blob').then(function (blob) {
              form.importImage(filename, blob)
            })
          }

          if (filename.startsWith('attachments/') && filename !== 'attachments/') {
            zip.files[filename].async('blob').then(function (blob) {
              form.importAttachment(filename, blob)
            })
          }
        })
      })
    }
  },
  watch: {
    '$i18n.locale': function () {
      if (this.hasErrors) {
        this.validate()
      }
    }
  }
}
</script>
